/**
 * SwapProgress: the cross-chain swap stepper and the global tracker gate.
 *
 * Two surfaces share this file because they read the same persisted pendingSwaps
 * slice. `SwapStepper` renders one swap's 3-step progress (origin confirmed,
 * bridging, delivered) with a live ETA countdown, source and destination
 * explorer links, the received amount on delivery, distinct non-error refunded
 * and settling states, and a taking-longer overlay. The Swap screen embeds it
 * for the swap the
 * user just started. `SwapTrackerGate` is the always-mounted global component
 * that resumes polling on boot and shows a compact banner for any in-flight swap
 * regardless of which screen is open, which is what makes a fill that lands after
 * the user navigates away still complete and toast.
 *
 * All progress data comes from the store record the poll engine (lib/swapTracker)
 * writes, so these components hold no polling state of their own.
 */

import { useEffect, useState } from 'react';

import { useSlice } from '@/store/hooks';
import type { PendingSwap } from '@/lib/types';
import {
  toUiState,
  stepIndexFor,
  explorerTxUrl,
  shortHash,
  type SwapUiState,
} from '@/lib/swapStatus';
import { isDelayed, resumeAll, stop, stopAll } from '@/lib/swapTracker';
import { translate } from '@/i18n';
import { reconcileOrigins } from '@/lib/originResume';
import { presentOrigin, blocksNewAttempt } from '@/lib/originStatus';
import { removePendingSwap } from '@/store/store';

/**
 * A ticking clock hook that re-renders every second while `active`, so the ETA
 * countdown and the delayed threshold recompute without the poll engine driving
 * React. Returns the current epoch milliseconds. When inactive it settles and
 * stops the interval, so a terminal swap costs no timer.
 */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [active]);
  return now;
}

/** Format remaining seconds as "1m 05s" or "12s", clamped at zero. */
function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
}

/** A transaction hash rendered as an explorer link, or plain text when unknown. */
function ExplorerLink({ chainId, hash }: { chainId: number; hash: string }) {
  const url = explorerTxUrl(chainId, hash);
  if (!url) return <span className="swap-stepper__hash">{shortHash(hash)}</span>;
  return (
    <a className="swap-stepper__hash" href={url} target="_blank" rel="noopener noreferrer">
      {shortHash(hash)} ↗
    </a>
  );
}

/**
 * The 3-step stepper for one swap. The furthest reached step is derived from the
 * canonical UI state, so a terminal off-ramp (refunded, failed) is styled apart
 * from a delivered swap rather than falsely lighting the delivered row.
 */
export function SwapStepper({ swap }: { swap: PendingSwap }) {
  const uiState: SwapUiState = toUiState(swap.status, Boolean(swap.originTxHash));
  const terminal =
    uiState === 'delivered' ||
    uiState === 'refunded' ||
    uiState === 'settling' ||
    uiState === 'failed';
  const now = useNow(!terminal);
  const reached = stepIndexFor(uiState);
  const delayed = isDelayed(swap, now);

  // The ETA countdown baseline is the quote's estimated fill time from when the
  // swap started; once it elapses the overlay shows taking-longer rather than a
  // negative number, so the countdown is only shown while it is still positive.
  const etaMs = swap.etaSeconds && swap.etaSeconds > 0 ? swap.etaSeconds * 1000 : 0;
  const remainingMs = etaMs > 0 ? swap.startedAt + etaMs - now : 0;
  const showCountdown = !terminal && etaMs > 0 && remainingMs > 0;
  const stepLabels = [
    uiState === 'submitting' || uiState === 'confirming-origin'
      ? `Confirming on ${swap.fromChainKey}`
      : `Confirmed on ${swap.fromChainKey}`,
    `Delivering to ${swap.toChainKey}`,
    'Delivered',
  ] as const;

  return (
    <div className="swap-stepper" data-state={uiState}>
      <ol className="swap-stepper__steps" aria-label="Swap progress">
        {stepLabels.map((label, i) => {
          // A step is done when the swap has moved past it, current when it is the
          // one in progress, and upcoming otherwise. On a terminal off-ramp no
          // step is "current" so the row does not spin after settling.
          const status =
            i < reached || (uiState === 'delivered' && i <= reached)
              ? 'done'
              : i === reached && !terminal
                ? 'current'
                : i === reached && terminal
                  ? 'stopped'
                  : 'upcoming';
          return (
            <li key={label} className="swap-stepper__step" data-status={status}>
              <span className="swap-stepper__marker" aria-hidden="true" />
              <span className="swap-stepper__label">{label}</span>
            </li>
          );
        })}
      </ol>

      {/* THE ORIGIN AXIS, shown separately and above the provider line.
          What the CHAIN says about the transaction we signed — a different
          question from what Relay or Across says became of the bridge request,
          and one the stepper above cannot answer. It is rendered whenever the
          server has an answer, including the states that need attention: a
          reverted origin and an unconfirmable one are both things the user must
          be told, not spun on. */}
      {swap.originStatus ? (
        <p
          className="swap-stepper__note"
          data-origin={swap.originStatus}
          data-attention={presentOrigin(swap.originStatus).needsAttention ? 'true' : undefined}
        >
          {translate(presentOrigin(swap.originStatus).messageKey)}
        </p>
      ) : null}

      {/* The live line under the stepper: a countdown while bridging, a
          taking-longer note once the ETA elapses, and the terminal summaries. */}
      {uiState === 'delivered' ? (
        <p className="swap-stepper__note swap-stepper__note--ok">
          Delivered{swap.expectedOut ? ` ${swap.expectedOut} ${swap.outSymbol}` : ''} to your wallet.
        </p>
      ) : uiState === 'refunded' ? (
        <p className="swap-stepper__note swap-stepper__note--refunded">
          Refunded to your wallet. Your {swap.sendSymbol} was returned; nothing was lost.
        </p>
      ) : uiState === 'settling' ? (
        // Reuse the non-error delayed styling: the client stopped actively
        // polling or the provider reported a lapse, but the swap may still
        // settle and an unfilled swap is refunded, so this is not an error.
        <p className="swap-stepper__note swap-stepper__note--delayed">
          Taking longer than usual. You can keep following it in Activity. If it never fills, your{' '}
          {swap.sendSymbol} is refunded to your wallet.
        </p>
      ) : uiState === 'failed' ? (
        <p className="swap-stepper__note swap-stepper__note--bad">
          No {swap.outSymbol} was delivered. Check Activity for the origin transaction and refund status.
        </p>
      ) : delayed ? (
        <p className="swap-stepper__note swap-stepper__note--delayed">
          Taking longer than expected. Still bridging; this can happen on testnet.
        </p>
      ) : showCountdown ? (
        <p className="swap-stepper__note text-muted">
          Estimated {formatRemaining(remainingMs)} remaining.
        </p>
      ) : (
        <p className="swap-stepper__note text-muted">Bridging to {swap.toChainKey}.</p>
      )}

      {/* Source and destination explorer links, shown as each hash appears. */}
      {swap.originTxHash || swap.destTxHash ? (
        <dl className="swap-stepper__links">
          {swap.originTxHash ? (
            <div className="swap-stepper__link-row">
              <dt className="text-muted">Source</dt>
              <dd>
                <ExplorerLink chainId={swap.fromChainId} hash={swap.originTxHash} />
              </dd>
            </div>
          ) : null}
          {swap.destTxHash ? (
            <div className="swap-stepper__link-row">
              <dt className="text-muted">Destination</dt>
              <dd>
                <ExplorerLink chainId={swap.toChainId} hash={swap.destTxHash} />
              </dd>
            </div>
          ) : null}
        </dl>
      ) : null}
    </div>
  );
}

/**
 * The always-mounted tracker gate. On mount it resumes polling every persisted
 * in-flight swap so a reload or a navigation continues each one, then renders a
 * compact banner listing the active swaps regardless of the current screen. It
 * returns null when nothing is in flight, matching the other global gates that
 * render only when they have something to show. The completion and refund toasts
 * are fired by the poll engine on the terminal transition, so this component only
 * mirrors the live records.
 */
export function SwapTrackerGate() {
  const pending = useSlice('pendingSwaps');
  // The id whose LATCHED dismissal is pending explicit acknowledgment (B1).
  // Ordinary swaps close on one click; a record whose origin axis still says a
  // broadcast may be outstanding is the double-spend gate itself, and closing
  // it must be a stated decision, not a reflex on an X.
  const [ackId, setAckId] = useState<string | null>(null);

  // Resume polling once on mount. The gate is mounted only while authed and
  // unmounts on logout, so this runs on each fresh session and after a reload,
  // which is exactly when the persisted swaps need re-arming. On unmount (logout)
  // the loops are stopped so a signed-out tab holds no background poll; the
  // persisted records stay so a re-login resumes them.
  useEffect(() => {
    resumeAll();
    // Ask the SERVER what became of these swaps rather than trusting what this
    // device last wrote down. Fire-and-forget: a failed reconcile leaves the
    // client showing what it already had, which is the honest fallback.
    const controller = new AbortController();
    void reconcileOrigins({ signal: controller.signal });
    return () => {
      controller.abort();
      stopAll();
    };
  }, []);

  // The global gate owns background continuity only. The floating status banner
  // was removed because the Swap page now has one authoritative inline card and
  // Activity holds the durable record.
  return null;

  // The Swap screen renders its own inline stepper for the swap it just started, so
  // skip exactly that one to avoid showing it twice. Suppressing the banner for the
  // whole route instead would hide a swap that has NO inline stepper — a persisted
  // one after a reload, or a second concurrent swap — leaving its progress nowhere on
  // screen. Polling is unaffected either way; it runs in the engine, not this render.
  const swaps = Object.values(pending.byId).filter((swap) => swap.id !== pending.inlineId);
  if (swaps.length === 0) return null;

  return (
    <div className="swap-tracker" role="status" aria-live="polite">
      {swaps.map((swap) => (
        <div key={swap.id} className="swap-tracker__item">
          <div className="swap-tracker__route">
            <span className="swap-tracker__pair">
              {swap.sendSymbol} → {swap.outSymbol}
            </span>
            <span className="swap-tracker__chains text-muted">
              {swap.fromChainKey} → {swap.toChainKey}
            </span>
            {/*
              Dismiss. The banner used to be unclosable: it sat over every screen
              until the swap settled or the client's 30-minute cap elapsed, so a
              swap that could never settle — one whose origin never broadcast, say —
              held the bottom of the app hostage with no way out.

              Dismissing stops this swap's poll loop and drops its local record. It
              does NOT cancel anything on-chain and cannot: the origin transaction,
              if it broadcast at all, is already away, and the swap service keeps
              its own record either way. What the user is closing is a progress
              display, which is why this is a plain close control and not a
              confirm-you-want-to-abandon dialog. The swap stays visible in Activity.
            */}
            {swap.ambiguous && blocksNewAttempt(swap.originStatus) && ackId === swap.id ? (
              /*
                THE ACKNOWLEDGMENT (B1). For a latched record the X does not
                close anything — it swaps itself for this explicit, worded
                release. Only clicking the worded control drops the latch,
                because dropping it re-enables the swap form while a broadcast
                may still be outstanding: the user is asserting they checked
                their wallet history, which is the §7 instruction for the
                unknown state. A click elsewhere leaves the latch in place.
              */
              <button
                type="button"
                className="swap-tracker__dismiss swap-tracker__ack"
                onClick={() => {
                  setAckId(null);
                  stop(swap.id);
                  removePendingSwap(swap.id);
                }}
              >
                {translate('swap.origin.ackRelease')}
              </button>
            ) : (
            <button
              type="button"
              className="swap-tracker__dismiss"
              aria-label={
                swap.ambiguous && blocksNewAttempt(swap.originStatus)
                  ? translate('swap.origin.ackPrompt')
                  : `Dismiss ${swap.sendSymbol} to ${swap.outSymbol} swap progress`
              }
              onClick={() => {
                if (swap.ambiguous && blocksNewAttempt(swap.originStatus)) {
                  setAckId(swap.id);
                  return;
                }
                stop(swap.id);
                removePendingSwap(swap.id);
              }}
            >
              <svg
                viewBox="0 0 24 24"
                width={16}
                height={16}
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                aria-hidden="true"
                focusable="false"
              >
                <line x1="6" y1="6" x2="18" y2="18" />
                <line x1="18" y1="6" x2="6" y2="18" />
              </svg>
            </button>
            )}
          </div>
          <SwapStepper swap={swap} />
        </div>
      ))}
    </div>
  );
}
